import React, { useState, useEffect } from 'react'
import { Button, Form, Input, Select, Switch, Tabs, Alert } from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { apiRequest } from '../../helpers'

const apiUrl = process.env.REACT_APP_API

const MODELS = [
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { value: 'gpt-4.1', label: 'GPT-4.1' },
  { value: 'gpt-4', label: 'GPT-4' },
  { value: 'o3', label: 'O3' },
]

const TpmAgentConfig = () => {
  const [aiConnectors, setAiConnectors] = useState([])
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [jsonError, setJsonError] = useState(null)
  const [form] = Form.useForm()

  // State separat pentru textarea-uri (nu Form.Item)
  const [aiContext, setAiContext] = useState('')
  const [aiResponseFormat, setAiResponseFormat] = useState('')

  useEffect(() => {
    fetchAgent()
    fetchAiConnectors()
  }, [])

  const fetchAgent = async () => {
    const res = await apiRequest(`${apiUrl}tpm/agent`, 'GET')
    if (res?.data) {
      form.setFieldsValue({
        name: res.data.name || 'TPM Agent',
        aiConnectorId: res.data.aiConnectorId || undefined,
        aiModel: res.data.aiModel || 'gpt-4o',
        aiMaxTokens: res.data.aiMaxTokens || 4000,
        active: res.data.active ?? true,
      })
      setAiContext(res.data.aiContext || '')
      setAiResponseFormat(res.data.aiResponseFormat ? JSON.stringify(res.data.aiResponseFormat, null, 2) : '')
    }
  }

  const fetchAiConnectors = async () => {
    const res = await apiRequest(`${apiUrl}connector/ai?limit=100`, 'GET')
    if (res?.data?.data) setAiConnectors(res.data.data.filter(c => c.active))
  }

  const handleSave = async () => {
    setError(null)
    setJsonError(null)
    setSuccess(false)

    let parsedSchema = null
    if (aiResponseFormat) {
      try { parsedSchema = JSON.parse(aiResponseFormat) }
      catch (e) { setJsonError('JSON Schema invalid: ' + e.message); return }
    }

    const values = await form.validateFields()
    setSaving(true)
    try {
      const payload = {
        ...values,
        aiConnectorId: values.aiConnectorId ? Number(values.aiConnectorId) : null,
        aiMaxTokens: Number(values.aiMaxTokens),
        aiContext,
        aiResponseFormat: parsedSchema,
      }
      await apiRequest(`${apiUrl}tpm/agent`, 'POST', payload)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch { setError('Eroare la salvare.') }
    finally { setSaving(false) }
  }

  const tabs = [
    {
      key: 'general',
      label: 'General',
      children: (
        <Form form={form} layout="vertical">
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Item label="Nume agent" name="name">
                <Input />
              </Form.Item>
            </div>
            <div className="col-md-6" style={{ display: 'flex', alignItems: 'center', paddingTop: 30 }}>
              <Form.Item name="active" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch checkedChildren="Activ" unCheckedChildren="Inactiv" />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item label="Conector AI" name="aiConnectorId">
                <Select placeholder="-- Selecteaza --" allowClear>
                  {aiConnectors.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                </Select>
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item label="Model AI" name="aiModel">
                <Select>
                  {MODELS.map(m => <Select.Option key={m.value} value={m.value}>{m.label}</Select.Option>)}
                </Select>
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item label="Max Tokens" name="aiMaxTokens">
                <Input type="number" />
              </Form.Item>
            </div>
          </div>
        </Form>
      ),
    },
    {
      key: 'instructions',
      label: 'Instructiuni AI',
      children: (
        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Instructiuni pentru AI (system prompt)</label>
          <textarea
            className="form-control"
            rows={15}
            value={aiContext}
            onChange={e => setAiContext(e.target.value)}
            style={{ fontFamily: 'monospace', fontSize: 13, width: '100%' }}
            placeholder="Introdu instructiunile pentru AI..."
          />
          <small style={{ color: '#888' }}>Descrie agentului cum sa clasifice emailurile si ce informatii sa extraga.</small>
        </div>
      ),
    },
    {
      key: 'schema',
      label: 'Format raspuns (JSON Schema)',
      children: (
        <div>
          {jsonError && <Alert message={jsonError} type="error" showIcon style={{ marginBottom: 8 }} />}
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>JSON Schema pentru raspunsul structurat</label>
          <textarea
            className="form-control"
            rows={20}
            value={aiResponseFormat}
            onChange={e => setAiResponseFormat(e.target.value)}
            style={{ fontFamily: 'monospace', fontSize: 12, width: '100%' }}
            placeholder='{"type": "object", ...}'
          />
          <small style={{ color: '#888' }}>Schema JSON care defineste formatul raspunsului de la OpenAI. Trebuie sa fie JSON valid.</small>
        </div>
      ),
    },
  ]

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <strong style={{ fontSize: 16 }}>Configurare Agent Procesare Email</strong>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          {saving ? 'Se salveaza...' : 'Salveaza'}
        </Button>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 12 }} />}
      {success && <Alert message="Configuratie salvata cu succes!" type="success" showIcon style={{ marginBottom: 12 }} />}

      <Tabs items={tabs} />
    </div>
  )
}

export default TpmAgentConfig
